import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Info, Eye, ChevronRight } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Checkbox } from 'components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import Switch from 'components/ui/switch';
import { Card, CardContent } from 'components/ui/card';
import { PageHeader } from 'components/ui/page-header';
import { RichTextEditor } from 'modules/editor';
import * as TestModule from 'modules/test';
import { isMarketplaceTest } from 'modules/test/marketplace-policy';
import * as AttemptModule from 'modules/attempt';
import { useTranslation } from 'react-i18next';
import ResultPreviewModal from './ResultPreviewModal';
import GradingByTypeTable from './grading/GradingByTypeTable';
import AiAssistCard from './grading/AiAssistCard';
import RubricEditor, { type RubricCriterion } from './grading/RubricEditor';
import { tallyQuestionTypes, summarizeGrading } from './grading/gradingTypes';

type PassMarkUnit = '%' | 'points';

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</h3>
);

const FlagCheckbox: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}> = ({ checked, onChange, label }) => (
  <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} />
    {label}
  </label>
);

export const GradingSummaryPage: React.FC = () => {
  const { t } = useTranslation();
  const { testId } = useParams<{ testId: string }>();

  const [endMessage, setEndMessage] = useState('');
  const [redirectEnabled, setRedirectEnabled] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');

  const [passMarkEnabled, setPassMarkEnabled] = useState(true);
  const [passMarkValue, setPassMarkValue] = useState(50);
  const [passMarkUnit, setPassMarkUnit] = useState<PassMarkUnit>('%');

  const [passMessage, setPassMessage] = useState('');
  const [failMessage, setFailMessage] = useState('');

  const [showPercentage, setShowPercentage] = useState(true);
  const [showPoints, setShowPoints] = useState(true);
  const [showPassFailMessage, setShowPassFailMessage] = useState(true);
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);

  const { item: test, isLoading } = TestModule.Hooks.useSingle({
    id: testId || '',
    enabled: Boolean(testId),
  });
  const updateConfig = TestModule.Hooks.useUpdateConfig();
  const isMarketplace = isMarketplaceTest(test);

  const typeTallies = tallyQuestionTypes(
    (test as any)?.sections,
    (test as any)?.questions,
  );
  const hasManualQuestions = typeTallies.some((t) => t.manual);

  // AI-assist grading + rubric (12b). Persisted under config.grading.feedback so
  // the grading pane (2d) reads the confidence threshold. `feedbackRest` keeps any
  // other feedback keys intact across Save (the service replaces feedback wholesale).
  // Defaults to enabled (true) whenever manual/open-ended questions are present
  // and the card is available.
  const savedAiAssist = (test as any)?.config?.grading?.feedback?.aiAssist?.enabled;
  const [aiAssistEnabled, setAiAssistEnabled] = useState<boolean>(() =>
    savedAiAssist != null ? Boolean(savedAiAssist) : hasManualQuestions,
  );
  const [confidenceThreshold, setConfidenceThreshold] = useState(80);
  const [rubric, setRubric] = useState<RubricCriterion[]>([]);
  const feedbackRest = useRef<Record<string, unknown>>({});

  useEffect(() => {
    if (!test) return;
    const grading = (test as any)?.config?.grading;
    if (!grading) {
      setAiAssistEnabled(hasManualQuestions);
      return;
    }
    setEndMessage(grading.endMessage ?? '');
    if (grading.redirectEnabled != null) setRedirectEnabled(grading.redirectEnabled);
    if (grading.redirectUrl) setRedirectUrl(grading.redirectUrl);
    // Hydrate the pass-mark enabled state too — without this a saved "disabled"
    // round-trips back to "enabled" and a Save silently re-applies a threshold.
    if (grading.passMarkEnabled != null) setPassMarkEnabled(grading.passMarkEnabled);
    if (grading.passMark != null) setPassMarkValue(grading.passMark);
    if (grading.passMarkUnit) setPassMarkUnit(grading.passMarkUnit);
    if (grading.passMessage != null) setPassMessage(grading.passMessage);
    if (grading.failMessage != null) setFailMessage(grading.failMessage);
    if (grading.showPercentage != null) setShowPercentage(grading.showPercentage);
    if (grading.showPoints != null) setShowPoints(grading.showPoints);
    if (grading.showPassFailMessage != null) setShowPassFailMessage(grading.showPassFailMessage);
    if (grading.showCorrectAnswers != null) setShowCorrectAnswers(grading.showCorrectAnswers);
    // AI-assist + rubric live under grading.feedback; keep any unrelated keys.
    const feedback = (grading.feedback ?? {}) as Record<string, unknown>;
    const { aiAssist, rubric: savedRubric, ...rest } = feedback as {
      aiAssist?: { enabled?: boolean; confidenceThreshold?: number };
      rubric?: RubricCriterion[];
    } & Record<string, unknown>;
    feedbackRest.current = rest;
    if (aiAssist?.enabled != null) {
      setAiAssistEnabled(aiAssist.enabled);
    } else {
      setAiAssistEnabled(hasManualQuestions);
    }
    if (typeof aiAssist?.confidenceThreshold === 'number') setConfidenceThreshold(aiAssist.confidenceThreshold);
    if (Array.isArray(savedRubric)) setRubric(savedRubric);
  }, [test, hasManualQuestions]);

  const gradingSummary = summarizeGrading(typeTallies);
  // "18 of 24 questions grade automatically. 6 open-ended need a rubric."
  const gradingSubtitle =
    gradingSummary.total === 0
      ? t('grading_by_type_empty')
      : gradingSummary.manual > 0
        ? t('grading_summary_with_manual', {
            auto: gradingSummary.auto,
            total: gradingSummary.total,
            manual: gradingSummary.manual,
          })
        : t('grading_summary_all_auto', { auto: gradingSummary.auto, total: gradingSummary.total });

  const sumPoints = (questions?: any[]): number =>
    (questions ?? []).reduce((qAcc: number, q: any) => qAcc + (q.points ?? 1), 0);
  const sections = (test as any)?.sections as any[] | undefined;
  // Mirror tallyQuestionTypes: sum section questions, else fall back to
  // root-level questions (a root-question test otherwise shows a 0 max score).
  const totalPoints =
    sections && sections.length > 0
      ? sections.reduce((acc: number, s: any) => acc + sumPoints(s.questions), 0)
      : sumPoints((test as any)?.questions);
  const maxScore = totalPoints || 10;

  const handleSave = async () => {
    if (!testId) return;
    await updateConfig.mutateAsync({
      id: testId,
      step: 'grading',
      data: {
        endMessage,
        redirectEnabled,
        redirectUrl: redirectEnabled ? redirectUrl : undefined,
        passMarkEnabled: !isMarketplace && passMarkEnabled,
        passMark: !isMarketplace && passMarkEnabled ? passMarkValue : undefined,
        passMarkUnit: !isMarketplace && passMarkEnabled ? passMarkUnit : undefined,
        passMessage,
        failMessage,
        showPercentage,
        showPoints,
        showPassFailMessage: !isMarketplace && showPassFailMessage,
        showCorrectAnswers,
        // Persist AI-assist + rubric under feedback (whitelisted free-form object);
        // the grading pane (2d) reads feedback.aiAssist.confidenceThreshold.
        feedback: {
          ...feedbackRest.current,
          ...(hasManualQuestions
            ? { aiAssist: { enabled: aiAssistEnabled, confidenceThreshold } }
            : {}),
          rubric,
        },
      },
    });
  };

  // Sample data for the Preview — mirrors what buildResultSummary would return
  // for a passing respondent, using the current (unsaved) form values.
  const buildPreviewData = (): AttemptModule.Types.IApi.GuestResult.Response => {
    const passingPercent = !isMarketplace && passMarkEnabled
      ? passMarkUnit === 'points'
        ? Math.round((passMarkValue / maxScore) * 100)
        : passMarkValue
      : null;
    const samplePercentage = 90;
    const sampleScore = Math.round((maxScore * samplePercentage) / 100);
    const passed = passingPercent != null ? samplePercentage >= passingPercent : null;
    const durationMs = 6 * 60 * 1000;
    const submittedMs = Date.now();
    return {
      attemptId: 'preview',
      testTitle: (test as any)?.title ?? '',
      fullName: 'Jane Smith',
      phone: null,
      startedAt: new Date(submittedMs - durationMs).toISOString(),
      submittedAt: new Date(submittedMs).toISOString(),
      status: 'GRADED',
      visibility: 'shown',
      score: sampleScore,
      maxScore,
      percentage: samplePercentage,
      passingScore: passingPercent,
      passed,
      durationMs,
      endMessage,
      message: passed == null ? null : passed ? passMessage : failMessage,
      flags: { showPercentage, showPoints, showPassFailMessage: !isMarketplace && showPassFailMessage, showCorrectAnswers },
    };
  };

  if (isLoading || !test) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      <PageHeader title={t('grading')} description={gradingSubtitle} />

      {/* By-type Auto/Manual summary */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <GradingByTypeTable tallies={typeTallies} />
        </CardContent>
      </Card>

      {/* AI-assist grading + per-question rubric (manual/AI-graded questions only) */}
      {hasManualQuestions && (
        <>
          <AiAssistCard
            enabled={aiAssistEnabled}
            onEnabledChange={setAiAssistEnabled}
            threshold={confidenceThreshold}
            onThresholdChange={setConfidenceThreshold}
          />
          <Card>
            <CardContent className="space-y-3 p-4 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">{t('rubric_criteria')}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t('rubric_criteria_desc')}</p>
                </div>
                {rubric.length > 0 && (
                  <span className="shrink-0 text-sm font-medium text-muted-foreground">
                    {t('rubric_total_points', {
                      count: rubric.reduce((sum, c) => sum + (Number.isFinite(c.points) ? c.points : 0), 0),
                    })}
                  </span>
                )}
              </div>
              <RubricEditor criteria={rubric} onChange={setRubric} />
            </CardContent>
          </Card>
        </>
      )}

      {/* Result display & messages — pass mark, end message, and respondent-visible
          info live only here, so they're kept but collapsed to match the clean design. */}
      <details className="group space-y-6">
        <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-foreground [&::-webkit-details-marker]:hidden">
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
          {t('grading_result_display_section')}
        </summary>

      {/* Test end message + redirection */}
      <Card>
        <CardContent className="space-y-4 p-6">
          <SectionLabel>{t('test_end_message_and_redirection')}</SectionLabel>
          <p className="text-sm text-muted-foreground">
            {t('configure_a_message_to_be_displayed_to_all_respondents_at_the_end_of_the_test_regardless_of_its_results')}
          </p>
          <RichTextEditor value={endMessage} onChange={setEndMessage} preset="answer" minHeight={100} />
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">
              {t('set_redirection_to_an_external_website_at_the_end_of_the_test')}
            </label>
            <Switch checked={redirectEnabled} onChange={setRedirectEnabled} />
          </div>
          {redirectEnabled && (
            <Input
              value={redirectUrl}
              onChange={(e) => setRedirectUrl(e.target.value)}
              placeholder={t('https')}
              className="max-w-md"
            />
          )}
        </CardContent>
      </Card>

      {/* Grading criteria */}
      {!isMarketplace && <Card>
        <CardContent className="space-y-4 p-6">
          <SectionLabel>{t('grading_criteria')}</SectionLabel>
          <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {t('define_grading_criteria_now_or_when_all_test_scores_are_available')}
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('maximum_possible_score_100_percent')}
            {totalPoints}
            {t('p.')}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-foreground">{t('pass_mark')}</span>
            <Switch checked={passMarkEnabled} onChange={setPassMarkEnabled} />
            {passMarkEnabled && (
              <>
                <Input
                  type="number"
                  min={0}
                  value={passMarkValue}
                  onChange={(e) => setPassMarkValue(Number(e.target.value) || 0)}
                  className="w-20"
                />
                <Select value={passMarkUnit} onValueChange={(v) => setPassMarkUnit(v as PassMarkUnit)}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="%">%</SelectItem>
                    <SelectItem value="points">{t('points')}</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
          {/* Grade ranges — deferred. Shown disabled so it doesn't pretend to work. */}
          <div className="flex items-center justify-between opacity-60">
            <label className="text-sm font-medium text-foreground">
              {t('define_grade_ranges_based_on_points_or_percents')}
              <span className="ml-2 text-xs text-muted-foreground">({t('coming_soon')})</span>
            </label>
            <Switch checked={false} onChange={() => undefined} disabled />
          </div>
        </CardContent>
      </Card>}

      {/* Information for respondents */}
      <Card>
        <CardContent className="space-y-4 p-6">
          <SectionLabel>{t('information_for_respondents')}</SectionLabel>
          <p className="text-sm text-muted-foreground">
            {t('choose_what_information_to_show_to_respondents_at_the_end_of_the_test')}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            <FlagCheckbox checked={showPercentage} onChange={setShowPercentage} label={t('percentage_score')} />
            <FlagCheckbox checked={showPoints} onChange={setShowPoints} label={t('points_score')} />
            {!isMarketplace && <FlagCheckbox
              checked={showPassFailMessage}
              onChange={setShowPassFailMessage}
              label={t('pass_or_fail_message')}
            />}
            <FlagCheckbox
              checked={showCorrectAnswers}
              onChange={setShowCorrectAnswers}
              label={t('correct_answers_to_questions')}
            />
          </div>

          {!isMarketplace && showPassFailMessage && (
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">{t('message_for_users_who_have_passed')}</label>
                <RichTextEditor value={passMessage} onChange={setPassMessage} preset="answer" minHeight={90} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">{t('message_for_users_who_have_failed')}</label>
                <RichTextEditor value={failMessage} onChange={setFailMessage} preset="answer" minHeight={90} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

        <Button variant="outline" className="w-full sm:w-auto" onClick={() => setPreviewOpen(true)}>
          <Eye className="mr-2 h-4 w-4" />
          {t('result_preview_button')}
        </Button>
      </details>

      <Button onClick={handleSave} disabled={updateConfig.isPending}>
        {updateConfig.isPending && (
          <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        )}
        {t('save')}
      </Button>

      <ResultPreviewModal isOpen={previewOpen} onClose={() => setPreviewOpen(false)} data={buildPreviewData()} />
    </div>
  );
};

export default GradingSummaryPage;
